import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchLeagueDetail } from '@/api';
import MatchCard from '@/components/match/MatchCard';
import LeagueSEOContent from '@/components/seo/LeagueSEOContent';
import LeagueStandings from '@/components/league/LeagueStandings';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { ChevronRight } from 'lucide-react';

export default function LeaguePage() {
  const { sport, league, slug } = useParams();
  const leagueSlug = league || slug;
  const { t } = useTranslation();
  const { currentLang, buildHreflang, getLocalizedPath } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueSlug) return;
    setLoading(true);
    fetchLeagueDetail(leagueSlug).then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [leagueSlug]);

  if (loading) return <LoadingPage />;
  if (!data?.league) return <div className="container mx-auto px-4 py-8 text-slate-400">{t('common.notFound')}</div>;

  const { league: leagueData, matches: rawMatches } = data;
  const hreflangTags = buildHreflang('league', leagueData.slug);

  const statusOrder = { live: 0, prematch: 1, finished: 2, cancelled: 3 };
  const matches = [...(rawMatches || [])].sort((a, b) => {
    const aStatus = statusOrder[a.status] ?? 2;
    const bStatus = statusOrder[b.status] ?? 2;
    if (aStatus !== bStatus) return aStatus - bStatus;
    if (a.status === 'prematch') {
      const aHasOdds = (a.markets_count || 0) > 0 ? 1 : 0;
      const bHasOdds = (b.markets_count || 0) > 0 ? 1 : 0;
      if (aHasOdds !== bHasOdds) return bHasOdds - aHasOdds;
    }
    return new Date(a.start_time) - new Date(b.start_time);
  });

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="league-page">
      <SEOHead
        title={t('league.title', { name: leagueData.name })}
        description={`Compare ${leagueData.name} odds. Find best odds, value bets and surebets.`}
        hreflang={hreflangTags}
      />

      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
          <Link to={`/${currentLang}`} className="hover:text-slate-300">{t('nav.home')}</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/${currentLang}/${getLocalizedPath('football').split('/').pop()}`} className="hover:text-slate-300 capitalize">{t('nav.football')}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-slate-300">{leagueData.name}</span>
        </div>
        <div className="flex items-center gap-4">
          {leagueData.logo_url && <img src={leagueData.logo_url} alt={leagueData.name} className="w-12 h-12 md:w-14 md:h-14 object-contain drop-shadow-lg" />}
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
              {leagueData.name}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{leagueData.country} &middot; {leagueData.season}</p>
          </div>
        </div>
      </div>

      <LeagueSEOContent slug={leagueSlug} />

      <LeagueStandings leagueId={leagueSlug} />

      <section data-testid="league-matches">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {matches?.map(match => (
            <MatchCard key={match.id} match={match} showLeague={false} />
          ))}
        </div>
        {!matches?.length && <p className="text-slate-500 text-sm">{t('common.matchesScheduled')}</p>}
      </section>
    </div>
  );
}
